import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconInformationOutline } from '@medflex/components/stories/Icons';
import Sessions from '@medflex/components/stories/Sessions';
import SessionsList from '@medflex/components/stories/Sessions/SessionsList';
import SessionsListItem from '@medflex/components/stories/Sessions/SessionsListItem';
import SessionsFAQ from '@medflex/components/stories/Sessions/SessionsFAQ';
import SupportAndFeedback from '@medflex/components/stories/Sessions/SupportAndFeedback';
import { useHistory } from 'react-router';
import useDisableRubberBandScrolling from '@medflex/hooks/useDisableRubberBandScrolling';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import useLocationSearchParams from '@medflex/hooks/useLocationSearchParams';
import { CALL_INFO_CENTER_URLS, PRECALL_TEST_URL } from '../../constants';
import useFeatures from '../../hooks/useFeatures';
import useSessionMeetingsList from './useSessionMeetingsList';
import useModal from '../../hooks/useModal';
import Actions from './Actions';
import Details from './Details';
import useSupportContact from '../../hooks/useSupportContact';
import SessionLimitBanner from './SessionLimitBanner';
import NoResult from './NoResults';
import { FILTER_VALUES } from './constants';
import Filters from './Filters';
import { MODALS } from '../../hooks/useIsModal';

const SessionsListModule = () => {
  const { filter = FILTER_VALUES.FUTURE } = useLocationSearchParams();
  const { pastMeetings, futureMeetings, success } = useSessionMeetingsList();
  const supportContact = useSupportContact();
  const history = useHistory();
  const divRef = useDisableRubberBandScrolling();
  const { t } = useTranslation('global', { keyPrefix: 'sessions.SessionsListModule' });
  const { open } = useModal();
  const [sessionMeetings] = useFeatures([FEATURE_KEYS.SESSION_MEETING]);
  const sortingOptions = {
    scheduledAtAsc: (a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt),
    scheduledAtDesc: (a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt),
  };

  const handleSessionCreate = () => {
    open({ modal: MODALS.videoauswahl });
  };

  const handleFilterChanged = ({ value }) => {
    history.push(`?filter=${value}`);
  };

  const handleTestStartClick = () => {
    window.open(PRECALL_TEST_URL, '_blank', 'noreferrer, noopener');
  };

  const handleHelpDeskSessionsClick = () => {
    const helpDesk = 'https://medflex.de/hilfe/behandler-video/videotermine-durchfuehren/';
    window.open(helpDesk, '_blank', 'noreferrer, noopener');
  };

  const handleHelpDeskAccounting = () => {
    const helpDesk =
      'https://medflex.de/hilfe/behandler-video/wichtige-hinweise-fuer-die-beantragung-und-nutzung-von-videosprechstunden/';
    window.open(helpDesk, '_blank', 'noreferrer, noopener');
  };

  const handleSupportChatClick = () => {
    history.push(`/chat/${supportContact.id}`);
  };

  const selectedMeetings = useMemo(
    () =>
      filter === FILTER_VALUES.PAST
        ? pastMeetings.sort(sortingOptions.scheduledAtDesc)
        : futureMeetings.sort(sortingOptions.scheduledAtAsc),
    [filter, pastMeetings, futureMeetings],
  );

  return (
    <div
      data-component="SessionsListModule"
      className="flex justify-start flex-1 overflow-y-auto mb-8"
      ref={divRef}
    >
      <div className="h-full w-full p-4 lg:p-8 max-w-6xl">
        <header className="flex items-center mb-6">
          <h1 className="text-2xl leading-none font-semibold">{t('title')}</h1>
          <a
            href={CALL_INFO_CENTER_URLS.physician.howToSchedule}
            target="_blank"
            rel="noopener noreferrer"
            className="flex text-blue-500 ml-2"
          >
            <IconInformationOutline size="6" />
          </a>
        </header>

        <Sessions
          onCreate={handleSessionCreate}
          filter={filter}
          onFilterChanged={handleFilterChanged}
          className="mb-8"
        >
          {sessionMeetings && sessionMeetings.limit && (
            <SessionLimitBanner
              active={sessionMeetings.active}
              limit={sessionMeetings.limit}
              count={sessionMeetings.count}
            />
          )}
          <div className="mb-4">
            <Filters />
          </div>
          <SessionsList
            placeholder={<NoResult />}
            hasDurationCol={filter === FILTER_VALUES.PAST}
            className="mb-6"
          >
            {success &&
              selectedMeetings.length > 0 &&
              selectedMeetings.map(
                ({
                  id,
                  provider,
                  title,
                  scheduledAt,
                  establishedAt,
                  participants,
                  duration,
                  scheduledEndedAt,
                  type,
                  attendee,
                }) => (
                  <SessionsListItem
                    className="relative"
                    key={id}
                    details={<Details id={id} title={title} participants={participants} />}
                    scheduledAt={scheduledAt}
                    type={type}
                    attendee={attendee}
                    duration={duration}
                    scheduledEndedAt={scheduledEndedAt}
                    hasDurationCol={filter === FILTER_VALUES.PAST}
                    actions={
                      filter === FILTER_VALUES.FUTURE && (
                        <Actions
                          started={!!establishedAt}
                          id={id}
                          participants={participants}
                          scheduledAt={scheduledAt}
                          type={type}
                        />
                      )
                    }
                  />
                ),
              )}
          </SessionsList>
          {filter === FILTER_VALUES.PAST && (
            <div className="text-center text-sm text-slate-600">{t('deletion_info')}</div>
          )}
        </Sessions>
        {filter === FILTER_VALUES.FUTURE && (
          <>
            <SessionsFAQ
              className="mb-6"
              onStartDeviceTestClick={handleTestStartClick}
              onHelpDeskSessionsClick={handleHelpDeskSessionsClick}
              onHelpDeskAccountingClick={handleHelpDeskAccounting}
              expanded
            />
            <SupportAndFeedback onClickSupportChat={handleSupportChatClick} />
          </>
        )}
      </div>
    </div>
  );
};

SessionsListModule.defaultProps = {};

SessionsListModule.propTypes = {};

export default SessionsListModule;
